<template>
  <!-- A small trend chart for the overview metric tiles. Vuetify's v-sparkline can
       draw EITHER a filled area OR a line (never both) and it does NOT scale to its
       container — so we (1) stack a faint fill behind a crisp line, (2) pin both to
       a fixed-height box, and (3) force the SVGs to stretch to that box via
       preserveAspectRatio. Data is downsampled so dense ranges stay readable. -->
  <div class="vds-spark" :style="{ height: `${height}px` }" role="presentation" aria-hidden="true">
    <v-sparkline
      class="vds-spark__fill"
      :value="points"
      :color="color"
      fill
      :smooth="smooth"
      :line-width="0"
      :padding="padding"
      :width="viewWidth"
      :height="height"
    />
    <v-sparkline
      class="vds-spark__line"
      :value="points"
      :color="color"
      :smooth="smooth"
      :line-width="lineWidth"
      :padding="padding"
      :width="viewWidth"
      :height="height"
      stroke-linecap="round"
      auto-draw
      :auto-draw-duration="duration"
    />
  </div>
</template>

<script>
export default {
  name: 'KvmSparkline',
  props: {
    value: { type: Array, default: () => [] },
    color: { type: String, default: 'primary' },
    height: { type: [Number, String], default: 44 },
    lineWidth: { type: [Number, String], default: 2 },
    smooth: { type: [Number, String], default: 4 },
    padding: { type: [Number, String], default: 4 },
    duration: { type: [Number, String], default: 600 },
    // Cap on rendered points — dense ranges (e.g. 24h ≈ 288 samples) get averaged
    // down to this many buckets so the line reads as a trend, not noise.
    maxPoints: { type: Number, default: 48 },
  },
  data: () => ({ viewWidth: 240 }),
  computed: {
    points () {
      const v = (this.value || []).filter(n => n != null)
      if (v.length <= this.maxPoints) return v
      const bucket = Math.ceil(v.length / this.maxPoints)
      const out = []
      for (let i = 0; i < v.length; i += bucket) {
        const slice = v.slice(i, i + bucket)
        out.push(slice.reduce((a, b) => a + b, 0) / slice.length)
      }
      return out
    },
  },
  watch: {
    points () { this.$nextTick(this.stretch) },
  },
  mounted () { this.stretch() },
  methods: {
    // v-sparkline renders a fixed-ratio SVG; let it fill the box edge-to-edge.
    stretch () {
      this.$el.querySelectorAll('svg').forEach(svg => svg.setAttribute('preserveAspectRatio', 'none'))
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.vds-spark {
  position: relative;
  width: 100%;
}
.vds-spark__fill,
.vds-spark__line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}
/* The fill is just a soft wash beneath the line. */
.vds-spark__fill {
  opacity: 0.14;
}

// The line uses v-sparkline's auto-draw (animated stroke-dashoffset). It's
// purely decorative, so cut the motion — and reveal the full line — for users
// who prefer reduced motion.
@include kvm-reduced-motion {
  ::v-deep .vds-spark__line svg path {
    transition: none !important;
    stroke-dashoffset: 0 !important;
  }
}
</style>
